import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { TranslateService } from '@ngx-translate/core';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

registerLocaleData(localeEs, 'es');

export type Lang = 'en' | 'es';

const KEY = 'tto.lang';

/**
 * El idioma de la persona que entra. El chofer lo elige en su perfil (se guarda en Users.PreferredLanguage), y con eso
 * salen en español sus pantallas (menú, Analytical, Pay Advance, My Payments, perfil), su estado de pago (pantalla,
 * PDF y correo) y el acuerdo del adelanto. Las pantallas de la oficina siguen en inglés.
 *
 * En las plantillas: {{ L.t('Pay advance', 'Adelanto de pago') }} y para fechas: date:'MMM d':undefined:L.locale
 */
@Injectable({ providedIn: 'root' })
export class LangService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  readonly lang = signal<Lang>(LangService.stored());
  /** true cuando la persona ya eligió (en el servidor); si no, el panel del chofer le pregunta una vez. */
  readonly chosen = signal<boolean>(true);
  private loaded = false;

  constructor() {
    this.translate.setDefaultLang('en');
    this.apply(this.lang());
  }

  get es(): boolean { return this.lang() === 'es'; }
  get locale(): string { return this.es ? 'es' : 'en-US'; }

  /** El texto en el idioma de la persona. */
  t(en: string, es: string): string {
    return this.lang() === 'es' ? es : en;
  }

  /** Lee el idioma guardado de la persona (una vez por sesión, o de nuevo con force). */
  load(force = false): void {
    if ((this.loaded && !force) || !localStorage.getItem('token')) return;
    this.loaded = true;
    this.http.get<{ language: Lang; chosen: boolean }>(`${environment.apiUrl}/User/me/language`).subscribe({
      next: r => { this.chosen.set(!!r?.chosen); this.apply(r?.language === 'es' ? 'es' : 'en'); },
      error: () => { this.loaded = false; }
    });
  }

  /** La persona cambia su idioma: se ve al momento y se guarda en su cuenta. */
  set(lang: Lang): Observable<{ language: Lang; message: string }> {
    this.apply(lang);
    this.chosen.set(true);
    return this.http.put<{ language: Lang; message: string }>(`${environment.apiUrl}/User/me/language`, { language: lang })
      .pipe(tap({ error: () => {} }));
  }

  /** Al salir de la cuenta: la próxima persona empieza en inglés hasta que se lea su idioma. */
  reset(): void {
    this.loaded = false;
    this.apply('en');
  }

  private apply(lang: Lang): void {
    this.lang.set(lang);
    try { localStorage.setItem(KEY, lang); } catch { /* sin almacenamiento */ }
    this.translate.use(lang);
    try { document.documentElement.lang = lang; } catch { /* sin documento */ }
  }

  private static stored(): Lang {
    try { return localStorage.getItem(KEY) === 'es' ? 'es' : 'en'; } catch { return 'en'; }
  }
}
